/**
 * CreateTestWizard -- 7-Step Test Creation Wizard
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 * Route: /test-admin/tests/create
 *
 * After launch, redirects to monitoring dashboard.
 */

import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, isEmailDeliveryError } from "../../services/apiClient";
import { showToast } from "../../lib/toast";
import { showEmailDeliveryError } from "../../lib/emailDeliveryToast";
import { WIZARD_STEPS } from "../../types";
import { WizardStepper } from "../../components/WizardStepper";

import { ScenarioSelectionStep } from "./ScenarioSelectionStep";
import { TestDetailsStep } from "./TestDetailsStep";
import { ParticipantsStep } from "./ParticipantsStep";
import { ConfigurationStep } from "./ConfigurationStep";
import { ReviewLaunchStep } from "./ReviewLaunchStep";

type WizardData = Record<string, unknown>;

export function CreateTestWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState<WizardData>({});

  const handleNext = useCallback(
    (stepData?: WizardData) => {
      if (stepData) {
        setWizardData((prev) => ({ ...prev, ...stepData }));
      }
      if (currentStep < WIZARD_STEPS.length - 1) {
        setCurrentStep((prev) => prev + 1);
      }
    },
    [currentStep]
  );

  const handleBack = useCallback((stepData?: WizardData) => {
    if (stepData) {
      setWizardData(prev => ({ ...prev, ...stepData }));
    }
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  // Returns true when the test launched (and we redirected to the monitor
  // page), false when it failed. ReviewLaunchStep uses the result to clear its
  // "Launching…" spinner: on failure we deliberately stay in the wizard rather
  // than navigating away, so the entered data is preserved for a retry.
  const handleLaunch = useCallback(async (): Promise<boolean> => {
    try {
      // Strip display-only fields before sending to API. Config fields
      // (back_navigation_enabled, min_option_points_threshold) are emitted
      // by ConfigurationStep in snake_case already, so they flow through
      // apiPayload as-is — no camelCase remapping here, or it would
      // override the real values.
      const { scenario_name, scenario_description, version_name, ...apiPayload } = wizardData;
      const test = await apiClient.post<{
        id: string;
        skipped_role_conflicts?: string[];
      }>("/api/tests", {
        ...apiPayload,
        scenario_type: apiPayload.scenario,
      });
      if (test.skipped_role_conflicts?.length) {
        showToast({
          variant: "warning",
          title: "Some participants were skipped",
          description: test.skipped_role_conflicts
            .map((e) => `${e} already has admin roles and cannot be added as a candidate`)
            .join(". "),
        });
      }
      // Keep the description generic: in Sc4 (Role TP) and Sc5 (baseline)
      // flows, participants are only notified after the TP/baseline step is
      // completed — so this must not claim notifications were already sent.
      showToast({
        variant: "success",
        title: "Test launched",
        description: "Your test has been launched successfully.",
      });
      navigate(`/test-admin/tests/${test.id}/monitor`);
      return true;
    } catch (err) {
      // Two distinct failure modes are reconciled here (origin's launch-retry
      // UX + gap STB-009's email surfacing):
      //  - Email-delivery failure (502 EMAIL_DELIVERY_FAILED): the test WAS
      //    created + launched; only the invitation emails failed (a recipient
      //    bounce, or an app-side Graph send error — the backend message names
      //    the recipient(s)/reason). Surface it and go to the tests list:
      //    re-opening the wizard would risk a DUPLICATE launch, since the test
      //    already exists. (Distinguishing recipient-bounce from a sending-side
      //    fault would need a backend pre-flight check / a separate error code;
      //    either way the test is already launched, so navigation is the same.)
      //  - Any other error: the launch itself failed and no test was created,
      //    so toast the reason and keep the admin on the wizard to correct and
      //    retry in place.
      if (isEmailDeliveryError(err)) {
        showEmailDeliveryError(err, { title: "Some invitations couldn't be sent" });
        navigate("/test-admin/tests");
        return false;
      }
      showToast({
        variant: "error",
        title: "Failed to launch test",
        description:
          err instanceof Error ? err.message : "Something went wrong. Please try again.",
      });
      return false;
    }
  }, [wizardData, navigate]);

  const stepComponents = [
    ScenarioSelectionStep,
    TestDetailsStep,
    ParticipantsStep,
    ConfigurationStep,
    ReviewLaunchStep,
  ];

  const StepComponent = stepComponents[currentStep];
  if (!StepComponent) return null;

  const isLastStep = currentStep === WIZARD_STEPS.length - 1;

  return (
    <main className="px-6 py-10 w-full max-w-5xl mx-auto">
      <WizardStepper steps={WIZARD_STEPS} currentStep={currentStep} />
      <div className="mt-6">
        <StepComponent wizardData={wizardData} onNext={isLastStep ? handleLaunch : handleNext} onBack={handleBack} />
      </div>
    </main>
  );
}

export default CreateTestWizard;
